import { NgModule, isDevMode, LOCALE_ID, APP_INITIALIZER } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ServiceWorkerModule } from '@angular/service-worker';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { LoaderComponent } from './components/loader/loader.component';
import { NgxSpinnerModule } from 'ngx-spinner';
import { ToolbarComponent } from './components/toolbar/toolbar.component';
import {
  HTTP_INTERCEPTORS,
  HttpClientModule
} from '@angular/common/http';
import {
  MAT_FORM_FIELD_DEFAULT_OPTIONS,
} from '@angular/material/form-field';
import { FooterComponent } from './components/footer/footer.component';
import { RouterModule } from '@angular/router';
import { ToastrModule } from 'ngx-toastr';
import localePl from '@angular/common/locales/pl';
import { DatePipe, registerLocaleData } from '@angular/common';
import { TimerComponent } from './components/timer/timer.component';
import { HomePageComponent } from './pages/home-page/home-page.component';
import { SharedModule } from './modules/shared/shared.module';
import {
  TranslateLoader,
  TranslateModule,
} from '@ngx-translate/core';
import { KeycloakAngularModule } from 'keycloak-angular';
import { OrdersPageComponent } from './pages/orders-page/orders-page.component';
import { MapPageComponent } from './pages/map-page/map-page.component';
import { ErrorInterceptor } from './interceptors/error.interceptor';
import { ReportsPageComponent } from './modules/reports/pages/reports-page/reports-page.component';
import { OrdersGridActionsComponent } from './pages/orders-page/components/orders-grid-actions/orders-grid-actions.component';
import { OrderFormComponent } from './pages/orders-page/components/order-form/order-form.component';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { UnauthorizedPageComponent } from './pages/unauthorized-page/unauthorized-page.component';
import { DataPageComponent } from './modules/data/pages/data-page/data-page.component';
import { TowersDataComponent } from './modules/data/components/towers-data/towers-data.component';
import { LinesDataComponent } from './modules/data/components/lines-data/lines-data.component';
import { SpansDataComponent } from './modules/data/components/spans-data/spans-data.component';
import { AppInitService } from './services/app-init.service';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MAT_MOMENT_DATE_FORMATS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { OrderDetailsComponent } from './pages/orders-page/components/order-details/order-details.component';
import { DBConfig, NgxIndexedDBModule } from 'ngx-indexed-db';
import { StorageAlertComponent } from './components/storage-alert/storage-alert.component';
import { OrderUsersDialogComponent } from './pages/orders-page/components/order-users-dialog/order-users-dialog.component';
import { TaskAssigneesDialogComponent } from './modules/go/pages/tasks-page/components/task-assignees-dialog/task-assignees-dialog.component';
import { SessionRefreshInterceptor } from './interceptors/session-refresh.interceptor';
import { createTranslateLoader } from './translate-static.loader';
import { OrderCardDialogComponent } from './pages/orders-page/components/order-card-dialog/order-card-dialog.component';
import { BuildInfoDialogComponent } from './components/build-info-dialog/build-info-dialog.component';

registerLocaleData(localePl);

function initializeApp(appInitService: AppInitService) {

    return () => appInitService.initializeApp();
}

const dbConfig: DBConfig  = {
  name: 'engo-db',
  version: 4,
  objectStoresMeta: [
    {
      store: 'ATTACHMENTS',
      storeConfig: {keyPath: 'id', autoIncrement: true},
      storeSchema: [
        {name: 'taskId', keypath: 'taskId', options: {unique: false}}
      ]
    },
    {
      store: 'TASKS',
      storeConfig: {keyPath: 'id', autoIncrement: true},
      storeSchema: [
        {name: 'taskId', keypath: 'taskId', options: {unique: false}},
        {name: 'projectId', keypath: 'projectId', options: {unique: false}},
        {name: 'formId_projectId', keypath: ['formId', 'projectId'], options: {unique: true}}
      ]
    },
    {
      store: 'TASK_DATA',
      storeConfig: {keyPath: 'id', autoIncrement: true},
      storeSchema: [
        {name: 'taskId', keypath: 'taskId', options: {unique: false}},
        {name: 'projectId', keypath: 'projectId', options: {unique: false}},
        {name: 'formId_projectId', keypath: ['formId', 'projectId'], options: {unique: true}}
      ]
    },
    {
      store: 'FORMS',
      storeConfig: {keyPath: 'id', autoIncrement: true},
      storeSchema: [
        {name: 'templateFormId', keypath: 'templateFormId', options: {unique: false}},
        {name: 'projectId', keypath: 'projectId', options: {unique: false}},
        {name: 'templateFormId_projectId', keypath: ['templateFormId', 'projectId'], options: {unique: true}}
      ]
    }
  ],
  migrationFactory: () => {
    return {
      2: (db, transaction) => {
        const store = transaction.objectStore('ATTACHMENTS');
        store.createIndex('attachmentId', 'attachmentId', { unique: false });
      },
      3: (db, transaction) => {
        const store = db.createObjectStore('FAILURES', {
          keyPath: 'id',
          autoIncrement: false
        });
        store.createIndex('byExternalId_Date', ['externalId', 'createdOn'], { unique: false });
        store.createIndex('byExternalIdStart_ExternalIdEnd_Date', ['externalIdStart', 'externalIdEnd', 'createdOn'], { unique: false });
      },
      4: (db, transaction) => {
        const store = transaction.objectStore('FAILURES');
        store.createIndex('byLatitude_Date', ['latitude', 'createdOn'], { unique: false });
      }
    }
  }
};


@NgModule({
  declarations: [
    AppComponent,
    LoaderComponent,
    ToolbarComponent,
    FooterComponent,
    TimerComponent,
    HomePageComponent,
    OrdersPageComponent,
    MapPageComponent,
    ReportsPageComponent,
    OrdersGridActionsComponent,
    OrderFormComponent,
    UnauthorizedPageComponent,
    DataPageComponent,
    TowersDataComponent,
    LinesDataComponent,
    SpansDataComponent,
    OrderDetailsComponent,
    StorageAlertComponent,
    OrderUsersDialogComponent,
    TaskAssigneesDialogComponent,
    OrderCardDialogComponent,
    BuildInfoDialogComponent
  ],
  imports: [
    SharedModule,
    BrowserModule,
    AppRoutingModule,
    KeycloakAngularModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader
      },
      isolate : false
    }),
    NgxIndexedDBModule.forRoot(dbConfig),
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerImmediately'
    }),
    HttpClientModule,
    BrowserAnimationsModule,
    NgxSpinnerModule,
    ToastrModule.forRoot(),
    RouterModule,
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      multi: true,
      deps: [AppInitService]
    },
    DatePipe,
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        floatLabel: 'auto',
        appearance: 'outline'
      }
    },
    {
      provide: LOCALE_ID,
      useValue: "pl-PL"
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: SessionRefreshInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true
    },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: {
        disableClose: true
      }
    },
    {
      provide: MAT_DATE_LOCALE,
      useValue: 'pl-PL'
    },
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: MAT_MOMENT_DATE_FORMATS
    },
    {
      provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS,
      useValue: { useUtc: true }
    },
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
